import React, { useCallback, useContext } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { InputField as BaseInputField } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { composeValidators, required } from '@ubeya/shared/utils/form';

const InputField = styled(BaseInputField)`
  input {
    ${({ $isRtl }) => $isRtl && ' direction: rtl'}
  }
`;

const UNSUPPORTED_CARD_PREFIXES = /^(34|37|30[0-5]|309|36|38|39)/;

const validateCardType = (value) => {
  const digits = (value || '').replace(/\s/g, '');

  return UNSUPPORTED_CARD_PREFIXES.test(digits) ? 'cardTypeNotSupported' : undefined;
};

// Luhn checksum: catches malformed/fake card numbers before they reach the backend
const isValidLuhn = (number) => {
  let sum = 0;
  let shouldDouble = false;

  for (let i = number.length - 1; i >= 0; i--) {
    let digit = Number(number[i]);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

const validateCardNumberChecksum = (value) => {
  const digits = (value || '').replace(/\s/g, '');

  return digits && !isValidLuhn(digits) ? 'invalidCardNumber' : undefined;
};

const validateCardNumber = composeValidators(required, validateCardType, validateCardNumberChecksum);

const AddCreditCardFields = () => {
  const { t, isRtl, countryCode } = useContext(ConfigContext);

  const {
    input: { onChange: onChangeCreditCard }
  } = useField('number');

  const handleChangeCreditCard = useCallback(
    (value) =>
      onChangeCreditCard(
        value
          .replace(/[^0-9]/gi, '')
          .replace(/(.{4})/g, '$1 ')
          .trim()
      ),
    [onChangeCreditCard]
  );

  return (
    <>
      <InputField
        $isRtl={isRtl}
        name="number"
        title={t('cardNumber')}
        onChange={handleChangeCreditCard}
        validate={validateCardNumber}
        controlledValue
      />

      <InputField name="expiry" title={t('expirationDate')} mask="00/00" validate={required} />
      <InputField name="cvv" title={t('securityCode')} validate={required} />
      {countryCode?.toLowerCase() === 'il' && <InputField name="identification" title={t('idNumberOptional')} />}
      <InputField name="cardHolderName" title={t('cardHolderName')} validate={required} />
    </>
  );
};

export default AddCreditCardFields;
